// iter291 · Pull-to-refresh wrapper for the Network home page.
//
// Detects a downward drag from the top of the scroll container, shows
// an Apple-style loading bubble that grows/spins as the user pulls,
// and once the drag crosses the trigger threshold fires the onRefresh
// callback (or falls back to `window.location.reload()`).
//
// Pure JS + CSS — no third-party gesture libs. Only listens while the
// user is actually at scrollTop === 0 so it never fights native scroll.

import { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';

const TRIGGER_PX  = 68;   // pull distance that fires the refresh
const MAX_PULL_PX = 110;  // clamp so the bubble never drifts too far
const RESIST_RATIO = 0.55; // dampen the pull so it feels rubbery

// iter301 · Kill-switch for the Capacitor native shell (iOS TestFlight /
// App Store). Inside the app, `window.location.reload()` wipes React
// state mid-render — users reported avatars vanishing and having to
// re-upload. Per product decision pull-to-refresh is disabled entirely
// on native iOS; web + PWA still get the gesture.
const isNativeShell = () => {
  if (typeof window === 'undefined') return false;
  try {
    if (window.Capacitor && typeof window.Capacitor.isNativePlatform === 'function') {
      return !!window.Capacitor.isNativePlatform();
    }
  } catch { /* SSR / access errors — treat as web */ }
  return false;
};

export default function PullToRefresh({
  onRefresh,
  children,
  disabled = false,
  className = '',
}) {
  const startYRef  = useRef(null);
  const activeRef  = useRef(false);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const doRefresh = useCallback(async () => {
    setRefreshing(true);
    // iter291 · Pull-to-refresh must be a FULL page reload, not a partial
    // data fetch. Jay's ask: the whole site refreshes so every stale
    // widget (banner counts, session state, ads, /me cache) rebuilds
    // from scratch — matches the native iOS/Twitter/Instagram behavior.
    setTimeout(() => {
      window.location.reload();
    }, 300); // small delay so the spinner has time to render
  }, []);

  useEffect(() => {
    if (disabled || isNativeShell()) return undefined;

    const onTouchStart = (e) => {
      if (refreshing) return;
      // Only engage if the page is scrolled to the top.
      const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
      if (scrollY > 0) return;
      if (!e.touches?.length) return;
      startYRef.current = e.touches[0].clientY;
      activeRef.current = true;
    };

    const onTouchMove = (e) => {
      if (!activeRef.current || refreshing) return;
      const y = e.touches[0].clientY;
      const dy = y - (startYRef.current || 0);
      if (dy <= 0) {
        setPull(0);
        return;
      }
      // Only claim the gesture once the user has meaningfully pulled —
      // avoids stealing tiny scroll jitters.
      const resisted = Math.min(MAX_PULL_PX, dy * RESIST_RATIO);
      setPull(resisted);
      if (resisted > 8 && e.cancelable) {
        e.preventDefault(); // block native rubber-band while we're driving
      }
    };

    const onTouchEnd = () => {
      if (!activeRef.current) return;
      activeRef.current = false;
      if (pull >= TRIGGER_PX) {
        doRefresh();
      } else {
        setPull(0);
      }
    };

    // Desktop fallback — allow ⌘+R users a wheel-drag from the top too.
    let wheelAccum = 0;
    let wheelTimer = null;
    const onWheel = (e) => {
      if (refreshing) return;
      const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
      if (scrollY > 0 || e.deltaY >= 0) { wheelAccum = 0; setPull(0); return; }
      wheelAccum += Math.abs(e.deltaY);
      const resisted = Math.min(MAX_PULL_PX, wheelAccum * 0.5);
      setPull(resisted);
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => {
        if (wheelAccum * 0.5 >= TRIGGER_PX) {
          doRefresh();
        } else {
          setPull(0);
        }
        wheelAccum = 0;
      }, 220);
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove',  onTouchMove,  { passive: false });
    window.addEventListener('touchend',   onTouchEnd);
    window.addEventListener('touchcancel', onTouchEnd);
    window.addEventListener('wheel',      onWheel,      { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove',  onTouchMove);
      window.removeEventListener('touchend',   onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
      window.removeEventListener('wheel',      onWheel);
      clearTimeout(wheelTimer);
    };
  }, [disabled, refreshing, pull, doRefresh]);

  const progress = Math.min(1, pull / TRIGGER_PX);
  // Slide the bubble in from above the viewport so it always clears the
  // top nav on mobile. At rest it sits at y=-40 (fully hidden); a full
  // pull lands it at y=48 with a subtle bounce.
  const bubbleY  = refreshing ? 56 : (pull > 0 ? Math.max(-40, -40 + pull * 1.35) : -40);
  const bubbleScale = refreshing ? 1 : 0.7 + progress * 0.3;
  const bubbleOpacity = refreshing ? 1 : Math.min(1, progress * 1.2);

  return (
    <div className={`relative ${className}`}>
      {/* Refresh bubble — floats over the top nav while pulling. High
          z-index guarantees it clears every fixed header + ad banner.
          Uses a proper drop-shadow so it reads elevated even against
          the amber Founding Athlete banner. */}
      <div
        aria-hidden={!refreshing && pull === 0}
        className="pointer-events-none fixed left-1/2 top-3 z-[9999] -translate-x-1/2 transition-[transform,opacity] duration-200 ease-out"
        style={{
          transform: `translate(-50%, ${bubbleY}px) scale(${bubbleScale})`,
          opacity: bubbleOpacity,
          paddingTop: 'env(safe-area-inset-top, 0px)',
        }}
        data-testid="pull-to-refresh-bubble"
      >
        <div
          className="w-12 h-12 rounded-full bg-black/85 backdrop-blur-xl ring-1 ring-white/20 flex items-center justify-center"
          style={{ boxShadow: '0 12px 40px -8px rgba(0,0,0,0.9), 0 0 0 1px rgba(255,255,255,0.08) inset' }}
        >
          <RefreshCw
            className={`w-5 h-5 text-white ${refreshing ? 'animate-spin' : ''}`}
            strokeWidth={2.4}
            style={!refreshing ? { transform: `rotate(${progress * 320}deg)` } : undefined}
          />
        </div>
      </div>
      {/* Content — nudge downward while pulling so it feels connected.
          Only apply the transform when actively pulling or refreshing —
          otherwise the transformed ancestor creates a new containing
          block that breaks `position: fixed` for every modal, sheet,
          and dropdown inside the page. */}
      <div
        style={(refreshing || pull > 0) ? {
          transform: `translateY(${refreshing ? 24 : pull * 0.35}px)`,
          transition: activeRef.current ? 'none' : 'transform 220ms ease',
        } : undefined}
      >
        {children}
      </div>
    </div>
  );
}
